import { useDebouncedCallback, useElementSize } from '@mantine/hooks';
import ClearIcon from '@mui/icons-material/Clear';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  Box,
  Button,
  ButtonBase,
  Fade,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Popover,
  Select,
  Stack,
  SxProps,
  TextField,
  Theme,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import React, { memo, useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isDeepEqual } from 'remeda';
import { getCommonPopoutSx, getSolidThemeGray } from 'utility/styles';
import {
  dropdownInputAnchorOrigin,
  dropdownInputElevation,
  dropdownInputTransformOrigin,
  getCloseButtonSx,
  getDropdownInputThreshold,
  getFilterTextfieldSx,
  getFooterContainerSx,
  getFormControlSx,
  getHeaderLabelTypoSx,
  getInfoTextIconSx,
  getRemoveButtonSx,
  getSelectSx,
  getTooltipSx,
  maxPopoverHeightVh,
  maxPopoverWidthPx,
  tooltipPopperProps,
} from './dropdownConsts';
import { PossibleEvent } from './SelectDropdown/SelectDropdownInput';
import { SelectRenderValueElem } from './SelectDropdown/SelectRenderValueElem';
import { PopperPropsExtended } from 'components/IconLabelFields/IconLabelTextField';

type TextDropdownInputProps = {
  inputLabel?: string;
  value?: string;
  updateValue: (event: PossibleEvent | any, value: string) => void;
  filterPlaceholderText?: string;
  formControlSx?: SxProps<Theme>;
  selectSx?: SxProps<Theme>;
  popoverPaperSx?: SxProps<Theme>;
  popoverMatchesFormControlWidth?: boolean;
  renderValueDisplay?: 'label' | 'chips';
  renderValueTooltip?: boolean;
  inputPlaceholderText?: string;
  formControlVariant?: 'standard' | 'outlined';
  /** default: false - if true, displays a MUI InputLabel on the FormControl with shrink behavior */
  showFormControlLabel?: boolean;
  applyChangesOnPopoverClose?: boolean;
  debounceUpdate?: number;
  disabled?: boolean;
  hidden?: boolean;
  infoText?: string;
  isDataPrivate?: boolean; // for logrocket masking
};

export const TextDropdownInput = memo(
  ({
    value = '',
    inputLabel = 'Input text...',
    inputPlaceholderText,
    filterPlaceholderText = inputLabel,
    updateValue,
    formControlSx,
    selectSx,
    popoverPaperSx,
    popoverMatchesFormControlWidth = false,
    renderValueDisplay = 'label',
    renderValueTooltip,
    formControlVariant = 'standard',
    showFormControlLabel = false,
    infoText = '',
    applyChangesOnPopoverClose = false,
    debounceUpdate = 0,
    disabled = false,
    hidden = false,
    isDataPrivate = false,
  }: TextDropdownInputProps) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const [popoverOpen, setPopoverOpen] = useState(false);
    const popoverAnchorRef = useRef<HTMLElement | null>(null);
    const { ref: elSizeRef, width: formCtrlWidth } = useElementSize();
    const textFieldId = useId(); // https://react.dev/reference/react/useId

    // Setup consts / state for applyChangesOnPopoverClose prop
    const [stagedValue, setStagedValue] = useState<string>('');
    const [internalValue, setInternalValue] = useState<string>(''); // internal non-debounced state

    // Setup debounced "updateValue" callback
    const debouncedUpdateValue = useDebouncedCallback(
      (event: PossibleEvent | any, newValue: string) => {
        updateValue(event, newValue);
      },
      debounceUpdate,
    );

    // Update value handler, determines which update function to call
    const updateValueHandler = (event: PossibleEvent, newValue: string) => {
      if (debounceUpdate) {
        debouncedUpdateValue(event, newValue);
      } else {
        updateValue(event, newValue);
      }
    };

    // Sync values passed in to internal state...
    useEffect(() => {
      // Don't update local state if there's nothing to update
      if (isDeepEqual(value, stagedValue)) {
        return;
      }
      // Don't update local state if the popover is open
      // and we're in applyChangesOnPopoverClose mode
      if (popoverOpen && applyChangesOnPopoverClose) {
        return;
      }

      // If we pass the guards, update the local states
      setStagedValue(value);
      setInternalValue(value);
    }, [stagedValue, popoverOpen, applyChangesOnPopoverClose, value]);

    // Conditional states consts
    const isDisabled = disabled;
    const isHidden = hidden;

    // Tooltip logic
    const showTooltip = renderValueTooltip || renderValueDisplay === 'label';
    const tooltipText = showTooltip ? internalValue : '';
    const tooltipTitle = !popoverOpen ? tooltipText : '';

    // Event handlers
    const handlePopoverOpen = () => {
      setPopoverOpen(true);
      setTimeout(() => {
        document.getElementById(textFieldId)?.focus();
      }, 0); // force to end of event loop / start of next tick - https://www.justjeb.com/post/why-does-settimeout-work
    };

    const handlePopoverClose = (
      event?: React.SyntheticEvent | any,
      reason?: 'backdropClick' | 'escapeKeyDown',
    ) => {
      if (applyChangesOnPopoverClose) {
        updateValue(event, stagedValue); // skip the update debouncing onClose
      }
      setPopoverOpen(false);
    };

    const handleTextChange = (event: PossibleEvent, newValue: string) => {
      setInternalValue(newValue);
      if (applyChangesOnPopoverClose) {
        setStagedValue(newValue);
      } else {
        updateValueHandler(event, newValue);
      }
    };

    const handleClearSelection = (event: PossibleEvent) => {
      setInternalValue('');
      if (applyChangesOnPopoverClose) {
        setStagedValue('');
      } else {
        updateValueHandler(event, '');
      }
    };

    return (
      <Tooltip
        title={tooltipTitle}
        disableInteractive={true}
        disableFocusListener={true}
        slotProps={{
          popper: {
            'data-private': isDataPrivate ? true : undefined,
          } as PopperPropsExtended,
          tooltip: {
            sx: {
              ...getTooltipSx(theme),
            },
          },
        }}
      >
        <FormControl
          size="small"
          variant={formControlVariant}
          ref={elSizeRef}
          disabled={isDisabled}
          hidden={isHidden}
          sx={{
            ...getFormControlSx(theme, popoverOpen),
            ...(typeof formControlSx === 'function'
              ? formControlSx(theme)
              : formControlSx),
            ...(isHidden ? { display: 'none' } : {}),
          }}
        >
          {showFormControlLabel && <InputLabel shrink>{inputLabel}</InputLabel>}
          <Select
            ref={popoverAnchorRef}
            multiple={false}
            disabled={isDisabled}
            hidden={isHidden}
            id={`${inputLabel}-text-input`}
            label={showFormControlLabel ? inputLabel : undefined}
            value={internalValue} // internal non-debounced state
            MenuProps={{ open: false }} // use custom popover
            open={popoverOpen}
            onOpen={handlePopoverOpen}
            sx={{
              ...getSelectSx(theme),
              ...(typeof selectSx === 'function' ? selectSx(theme) : selectSx),
            }}
            displayEmpty={true}
            renderValue={() => (
              <SelectRenderValueElem
                selectedChoices={
                  internalValue ? [{ value: internalValue, label: internalValue }] : []
                }
                placeholderText={
                  inputPlaceholderText ?? (showFormControlLabel ? '' : inputLabel)
                }
                renderValueDisplay={renderValueDisplay}
                isDataPrivate={isDataPrivate}
              />
            )}
          >
            {/**
             * To suppress the out-of-range value warning: add the empty default & always have the current value as a choice.
             */}
            <MenuItem
              value=""
              hidden={true}
              style={{ display: 'none' }}
              data-private={isDataPrivate ? true : undefined}
            />
            <MenuItem
              value={internalValue}
              hidden={true}
              style={{ display: 'none' }}
              data-private={isDataPrivate ? true : undefined}
            />
          </Select>

          <Popover
            anchorEl={popoverAnchorRef.current}
            open={popoverOpen && !isDisabled}
            hidden={isHidden}
            onClose={handlePopoverClose}
            anchorOrigin={dropdownInputAnchorOrigin}
            transformOrigin={dropdownInputTransformOrigin}
            elevation={dropdownInputElevation}
            marginThreshold={getDropdownInputThreshold(theme)}
            slotProps={{
              paper: {
                sx: {
                  display: 'grid',
                  gridTemplateRows: 'auto auto 1fr',
                  gridTemplateAreas: `'selectheader' 'selectmainarea' 'selectfooter'`,
                  container: 'selectgrid / normal',
                  overflow: 'hidden',
                  width: popoverMatchesFormControlWidth
                    ? formCtrlWidth
                    : maxPopoverWidthPx,
                  maxHeight: maxPopoverHeightVh,
                  gap: 1,
                  bgcolor: getSolidThemeGray(theme),
                  ...getCommonPopoutSx(theme),
                  ...(typeof popoverPaperSx === 'function'
                    ? popoverPaperSx(theme)
                    : popoverPaperSx),
                },
              },
            }}
          >
            <Stack
              direction="row"
              sx={{
                justifyContent: 'space-between',
                alignItems: 'center',
                gridArea: 'selectheader',
                borderBottom: `1px solid ${theme.palette.action.selected}`,
                p: 1,
              }}
            >
              <Tooltip
                title={infoText}
                arrow={false}
                disableInteractive={true}
                enterDelay={500}
                placement="bottom-start"
                slotProps={{
                  popper: tooltipPopperProps,
                }}
              >
                <Typography
                  sx={getHeaderLabelTypoSx}
                  aria-label={`${inputLabel} - ${infoText}`}
                >
                  {inputLabel}
                  {infoText ? <InfoOutlinedIcon sx={getInfoTextIconSx} /> : null}
                </Typography>
              </Tooltip>
              <Button
                variant="text"
                size="small"
                color="error"
                disabled={!internalValue}
                startIcon={<RemoveCircleOutlineIcon />}
                sx={getRemoveButtonSx}
                onClick={(event) => handleClearSelection(event)}
              >
                {t('blox.actions.remove')}
              </Button>
            </Stack>

            <Stack
              direction="column"
              sx={{
                gridArea: 'selectmainarea',
                px: 0.5,
              }}
            >
              <TextField
                fullWidth
                data-private={isDataPrivate ? true : undefined}
                autoComplete="off"
                id={textFieldId}
                value={internalValue}
                onChange={(event) => handleTextChange(event, event?.target?.value)}
                onKeyUp={(event) => {
                  const keyCodes = ['Enter', 13];
                  if (event.key && keyCodes.includes(event.key)) {
                    handlePopoverClose();
                  }
                }}
                placeholder={filterPlaceholderText}
                variant="outlined"
                size="small"
                margin="dense"
                sx={getFilterTextfieldSx}
                slotProps={{
                  input: {
                    endAdornment: (
                      <Fade in={Boolean(internalValue)}>
                        <Tooltip
                          title="Clear input"
                          disableInteractive
                        >
                          <InputAdornment position="end">
                            <ClearIcon
                              fontSize="small"
                              onClick={(event) => handleClearSelection(event)}
                            />
                          </InputAdornment>
                        </Tooltip>
                      </Fade>
                    ),
                  },
                }}
              />
            </Stack>

            <Stack
              sx={{
                gridArea: 'selectfooter',
                display: 'grid',
                gridTemplateColumns: '1fr auto',
                gridTemplateAreas: `"listinfo closebtn"`,
                alignItems: 'center',
                ...getFooterContainerSx(theme, true),
              }}
            >
              {/* Spacer to keep the close button location consistent with other advanced filters */}
              <Box sx={{ gridArea: 'listinfo' }} />

              <ButtonBase
                onClick={handlePopoverClose}
                sx={{
                  gridArea: 'closebtn',
                  ...getCloseButtonSx(theme),
                }}
              >
                {t('blox.actions.close')}
              </ButtonBase>
            </Stack>
          </Popover>
        </FormControl>
      </Tooltip>
    );
  },
);
TextDropdownInput.displayName = 'TextDropdownInput';
